import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * useAudition — plays a fixed-length clip of an audio file starting at a
 * user-picked offset, then auto-stops. Extracted from PreviewStartPicker so
 * the picker component stays visual-only.
 *
 * Returns: { playing, elapsed, play, stop, seek }
 *   • elapsed: 0…clipSec while playing, reset to 0 on stop
 *   • play():   loads the audio (lazy), seeks to `startSec`, plays
 *   • stop():   pauses + resets state
 *   • seek(v):  jump to a new start while playing (no restart needed)
 */
export default function useAudition(audioSrc, startSec, clipSec = 30) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const audioRef = useRef(null);
  const startRef = useRef(startSec);
  startRef.current = startSec;  // keep the timeupdate closure current without re-binding listeners

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setElapsed(0);
  };

  const ensureAudio = () => {
    if (audioRef.current || !audioSrc) return audioRef.current;
    const a = new Audio(audioSrc);
    a.preload = "auto";
    a.addEventListener("timeupdate", () => {
      const el = Math.max(0, (a.currentTime || 0) - startRef.current);
      setElapsed(el);
      if (el >= clipSec) stop();
    });
    a.addEventListener("ended", stop);
    a.addEventListener("error", () => { toast.error("Couldn't load audio preview"); stop(); });
    audioRef.current = a;
    return a;
  };

  const play = () => {
    if (!audioSrc) { toast.error("No audio file on this track"); return; }
    const a = ensureAudio();
    a.currentTime = startSec;
    a.play().then(() => setPlaying(true))
      .catch(() => toast.error("Playback blocked — tap play again"));
  };

  const seek = (v) => {
    if (playing && audioRef.current) {
      audioRef.current.currentTime = v;
      setElapsed(0);
    }
  };

  useEffect(() => () => stop(), []);  // cleanup on unmount

  return { playing, elapsed, play, stop, seek };
}
